import React, { useEffect, useRef } from 'react'
import cn from 'classnames'
import styles from 'components/quests/confirm.module.scss'
import api from 'api/api'
import {
    cancelExternalCheckout,
    openExternalCheckout,
    prepareExternalCheckout,
} from 'utils/nativeApp'
import {
    Image as ImageIcon,
    Video as VideoIcon,
    Music as MusicIcon,
    AlertCircle,
    CheckCircle2,
    AlertTriangle,
    Info,
    Sparkles,
    Zap,
    ArrowBigUp,
    Bitcoin,
} from 'lucide-react'

export default function Confirm({ modalParams, closeModal }) {
    const confirmVariant = modalParams?.confirmVariant || 'bsv'
    const confirmButtonClassName =
        confirmVariant === 'usd'
            ? styles.stripeButtonPrimary
            : confirmVariant === 'danger'
            ? styles.dangerButton
            : styles.bsvButton
    const primaryActionFirst = !!modalParams?.primaryActionFirst
    const hideConfirm = !!modalParams?.hideConfirm

    const didActRef = useRef(false)

    const onConfirm = () => {
        if (modalParams?.confirmDisabled) {
            return
        }
        didActRef.current = true
        closeModal()
        if (modalParams.onConfirm) modalParams.onConfirm()
    }

    const onCancel = () => {
        didActRef.current = true
        closeModal()
        if (modalParams.onCancel) modalParams.onCancel()
    }

    const onDismiss = () => {
        didActRef.current = true
        closeModal()
        if (modalParams.onDismiss) modalParams.onDismiss()
        else if (modalParams.onCancel) modalParams.onCancel()
    }

    const onExtraAction = extraAction => {
        if (extraAction?.disabled) {
            return
        }
        didActRef.current = true
        closeModal()
        if (extraAction?.onClick) {
            extraAction.onClick()
        }
    }

    const onSetupLink = link => {
        if (link?.disabled) {
            return
        }
        didActRef.current = true
        closeModal()
        if (link?.onClick) {
            link.onClick()
        }
    }

    const renderIcon = (size = 48) => {
        const iconProps = {
            size,
            strokeWidth: 1.5,
            className: styles.lucideIcon,
        }

        if (modalParams.iconType === 'image-gen') {
            return <ImageIcon {...iconProps} color="#a855f7" />
        } else if (modalParams.iconType === 'video-gen') {
            return <VideoIcon {...iconProps} color="#ec4899" />
        } else if (modalParams.iconType === 'audio-gen') {
            return <MusicIcon {...iconProps} color="#10b981" />
        } else if (modalParams.iconType === 'error') {
            return <AlertCircle {...iconProps} color="#ef4444" />
        } else if (modalParams.iconType === 'success') {
            return <CheckCircle2 {...iconProps} color="#22c55e" />
        } else if (modalParams.iconType === 'warning') {
            return <AlertTriangle {...iconProps} color="#f59e0b" />
        } else if (modalParams.iconType === 'info') {
            return <Info {...iconProps} color="#3b82f6" />
        } else if (modalParams.iconType === 'ai-gen') {
            return <Sparkles {...iconProps} color="#6366f1" />
        } else if (modalParams.iconType === 'upvalue') {
            return <ArrowBigUp {...iconProps} color="#22c55e" fill="#22c55e" />
        } else if (modalParams.iconType === 'bitcoin') {
            return (
                <Bitcoin
                    {...iconProps}
                    className={cn(styles.lucideIcon, styles.bitcoinIcon)}
                    color="var(--color-brand-bitcoin)"
                />
            )
        }

        // Default fallback icon
        return <Zap {...iconProps} color="#6366f1" />
    }

    const renderMessage = () => {
        // If message is a React element, return it directly
        if (React.isValidElement(modalParams.message)) {
            return modalParams.message
        }
        // Otherwise treat it as a string
        return <div className={styles.confirmMessageText}>{modalParams.message}</div>
    }

    useEffect(() => {
        const handleKeyDown = event => {
            if (event.key === 'Enter') {
                if (modalParams?.confirmOnEnter !== false) onConfirm()
            } else if (event.key === 'Escape') {
                onDismiss()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [modalParams])

    // If user closes modal via the top-right "X" (or clicking overlay),
    // ensure any awaiting promise resolves as cancel.
    useEffect(() => {
        return () => {
            if (!didActRef.current) {
                if (modalParams?.onDismiss) modalParams.onDismiss()
                else if (modalParams?.onCancel) modalParams.onCancel()
            }
        }
    }, [])

    const shouldShowStripePayFallback =
        modalParams?.showStripePay ||
        (modalParams?.confirmLabel === 'Create' &&
            (modalParams?.iconType === 'image-gen' || modalParams?.iconType === 'video-gen'))
    const modalKey = modalParams?.modalKey
    const modalTestId = modalKey ? `confirm-modal-${modalKey}` : 'confirm-modal'
    const confirmButtonTestId = modalKey
        ? `confirm-modal-${modalKey}-confirm-button`
        : 'confirm-modal-confirm-button'
    const cancelButtonTestId = modalKey
        ? `confirm-modal-${modalKey}-cancel-button`
        : 'confirm-modal-cancel-button'

    return (
        <div className={styles.confirmComp} data-testid={modalTestId}>
            {modalParams?.headerTitle ? (
                <div className={styles.compactHeader}>
                    {renderIcon(22)}
                    <span className={styles.compactHeaderTitle}>{modalParams.headerTitle}</span>
                </div>
            ) : (
                <div className={styles.confirmHeader}>
                    <div className={styles.iconWrapper}>{renderIcon()}</div>
                </div>
            )}
            <div className={styles.confirmMessageContainer}>{renderMessage()}</div>
            <div
                className={cn(
                    styles.confirmActions,
                    modalParams?.stackActions && styles.confirmActionsStacked,
                    modalParams?.inlineConfirmActions && styles.confirmActionsInline,
                )}
            >
                {!modalParams.hideCancel && (
                    <button
                        className={styles.cancelButton}
                        data-testid={cancelButtonTestId}
                        onClick={onCancel}
                    >
                        {modalParams.cancelMessage || 'Cancel'}
                    </button>
                )}

                {primaryActionFirst && !hideConfirm && (
                    <button
                        className={confirmButtonClassName}
                        onClick={onConfirm}
                        data-testid={confirmButtonTestId}
                        disabled={!!modalParams?.confirmDisabled}
                        style={
                            modalParams?.confirmDisabled
                                ? { opacity: 0.5, cursor: 'not-allowed' }
                                : {}
                        }
                    >
                        {modalParams.confirmLabel || 'Got it!'}
                    </button>
                )}

                {Array.isArray(modalParams.extraActions) &&
                    modalParams.extraActions.map((action, idx) => {
                        const variant = action?.variant || 'secondary'
                        const className =
                            variant === 'usd' || variant === 'primary'
                                ? styles.stripeButtonPrimary
                                : variant === 'bsv'
                                ? styles.bsvButton
                                : styles.stripeButton

                        return (
                            <button
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${action?.label || 'extra'}-${idx}`}
                                className={className}
                                disabled={!!action?.disabled}
                                style={
                                    action?.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                                }
                                onClick={() => onExtraAction(action)}
                            >
                                {action?.label}
                            </button>
                        )
                    })}

                {modalParams?.actionPrefix && (
                    <div className={styles.confirmActionPrefix}>{modalParams.actionPrefix}</div>
                )}

                {shouldShowStripePayFallback && !Array.isArray(modalParams.extraActions) && (
                    <button
                        className={styles.stripeButtonPrimary}
                        onClick={async () => {
                            closeModal()
                            const checkout = prepareExternalCheckout()
                            if (checkout.requiresPopup && !checkout.popup) {
                                alert('Could not open Stripe in your browser. Please try again.')
                                return
                            }
                            try {
                                const res = await api.createStripeAiBillingCheckoutSession()
                                if (res?.url) {
                                    if (!openExternalCheckout(res.url, checkout)) {
                                        cancelExternalCheckout(checkout)
                                        alert(
                                            'Could not open Stripe in your browser. Please try again.',
                                        )
                                    }
                                    return
                                }
                                cancelExternalCheckout(checkout)
                                alert(res?.error || 'Stripe error creating checkout session')
                            } catch (e) {
                                cancelExternalCheckout(checkout)
                                alert('Stripe error creating checkout session')
                            }
                        }}
                    >
                        Pay with Stripe
                    </button>
                )}

                {!primaryActionFirst && !hideConfirm && (
                    <button
                        className={confirmButtonClassName}
                        onClick={onConfirm}
                        data-testid={confirmButtonTestId}
                        disabled={!!modalParams?.confirmDisabled}
                        style={
                            modalParams?.confirmDisabled
                                ? { opacity: 0.5, cursor: 'not-allowed' }
                                : {}
                        }
                    >
                        {modalParams.confirmLabel || 'Got it!'}
                    </button>
                )}
            </div>

            {modalParams?.footerContent && (
                <div className={styles.confirmFooterContent}>{modalParams.footerContent}</div>
            )}

            {Array.isArray(modalParams?.setupLinks) && modalParams.setupLinks.length > 0 && (
                <div className={styles.setupLinks}>
                    {modalParams.setupLinks.map((link, idx) => (
                        <button
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${link?.label || 'setup'}-${idx}`}
                            className={
                                link?.variant === 'usd'
                                    ? styles.setupLinkUsd
                                    : link?.variant === 'bsv'
                                    ? styles.setupLinkBsv
                                    : styles.setupLink
                            }
                            disabled={!!link?.disabled}
                            onClick={() => onSetupLink(link)}
                        >
                            {link?.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
